// Third-party
import Head from 'next/head';
import { useRouter } from 'next/router';

// First-party
import { Slot, createPage } from '@lib/util/templateHelpers';
import { gameToLdJson } from '@lib/util/gameToLdJson';
import Meta from '@lib/components/head/Meta';

// Component lib
import shared from '@lib/components';

// Local
import gameApi from '@/apis/drupal/game';
import categoryApi from '@/apis/drupal/category';
import routerApi from '@/apis/drupal/router';
import FillScreenLayout from '@/layouts/FillScreenLayout';
import GameTileLayout from '@/layouts/GameTileLayout';

// Local components
import AllGamesBlock from '@/components/AllGamesBlock';

const { 
  Loader,  
  DocumentTitle 
} = shared;

export async function getServerSideProps ({ params }) {
  const route = await routerApi.translatePath(`/category/${params.category}`);
  if (route.error) return {
    notFound: true,
  };

  const categoryId = route.entity.uuid;

  const limit = process.env.APP_CONFIG.DEFAULT_GAME_LIMIT;
  const category = await categoryApi.getById(categoryId);
  const games = await gameApi.listByCategory(categoryId, limit, 0);
  
  return {
    props: {
      category,
      games
    },
  };
}

export default createPage('CategoryPage', {}, function CategoryPage ({ className }, props) {
  const router = useRouter();

  if (router.isFallback) {
    return (
      <FillScreenLayout className={className}>
        <Loader.Fill large />
      </FillScreenLayout>
    );
  }

  const fetchMoreGames = (limit, offset) => {
    return gameApi.listByCategory(props.category.id, limit, offset);
  };

  const description = props.category.descriptionSimple
    ? props.category.descriptionSimple
    : `${props.category.name} io Games | ${process.env.APP_CONFIG.DESCRIPTION}`;

  return (
    <GameTileLayout key={props.category.path} className={className}>
      <DocumentTitle title={props.category.name} />
	  	 <Meta
			  title={props.category.name}
			  description={description}
			  openGraph={{
				title: props.category.name,
				description: description,
				image: props.category.thumbnailUrl,
				type: 'website'
			  }}
			>
			<script 
				type='application/ld+json' 
				dangerouslySetInnerHTML={{ 
					__html: JSON.stringify({
					'@context': 'https://schema.org/',
					//'@type': 'WebSite',
					'@type': 'CollectionPage',
					'name': process.env.APP_CONFIG.DEFAULT_TITLE,
					'potentialAction': {'@type':'Action','name':'Play'},
					'url': process.env.APP_CONFIG.URL + '/category/' + router.query.category
					}, null, 2)
				}}
				/>
				<script 
				type='application/ld+json' 
				dangerouslySetInnerHTML={{ 
					__html: JSON.stringify({
					'@type': 'ItemList',
					'itemListElement': props.games.map((game, index) => ({
						'@type': 'ListItem',
						'position': index + 1,
						'url': process.env.APP_CONFIG.URL + game.path,
						'name': game.name
					// 'item': gameToLdJson(game)
					}))
					}, null, 2)
				}}
				/>
				<script 
				type='application/ld+json' 
				dangerouslySetInnerHTML={{ 
					__html: JSON.stringify({
					"@type": "BreadcrumbList",
					"itemListElement": [
						{
						"@type": "ListItem",
						"position": 1,
						"item": process.env.APP_CONFIG.URL,
						"name": "Games"
						},
						{
						"@type": "ListItem",
						"position": 2,
						"name": props.category.name + " IO Games"
						}
					],
					}, null, 2)
				}}
				/>

		</Meta>
      <AllGamesBlock feature games={props.games} fetchMoreGames={fetchMoreGames}>
        <Slot name='title'>{props.category.name} io Games</Slot>
        <Slot name='description'>{props.category.description}</Slot>
      </AllGamesBlock>
    </GameTileLayout>
  );
});

